export type ResourceCategory =
  | 'Flashcards'
  | 'Grammar'
  | 'Pronunciation'
  | 'Storybooks'
  | 'Songs'
  | 'Games'
  | 'Puzzles';

export type HubSectionTitle = ResourceCategory | 'Popular' | "What's New" | 'Premium';

export type WebResource = {
  slug: string;
  title: string;
  subtitle?: string;
  category: ResourceCategory;
  href: string;
  premium?: boolean;
  dialects?: Array<'hmonglus' | 'moob'>;
  cardCategory?: string;
  backgroundColor?: string;
  textColor?: string;
  nameColor?: string;
  badgeText?: string | null;
  badgeBackground?: string | null;
  addedOn?: string | null;
  publishedAt?: string | null;
  /** Hub-only collection with no bundled card module (favorites, stickers). */
  virtual?: boolean;
  mobileKey?: string;
};

const FLASH = { cardCategory: 'Flashcards', backgroundColor: '#E6E6FF', textColor: '#333380', badgeBackground: '#33338033' };
const GRAMMAR = { cardCategory: 'Grammar', backgroundColor: '#B2DBFF', textColor: '#2D6A99', badgeBackground: '#2D6A9933' };
const PRON = { cardCategory: 'Pronunciation', backgroundColor: '#FFEBB2', textColor: '#99802D', badgeBackground: '#99802D33' };
const STORY = { cardCategory: 'Storybook', backgroundColor: '#FFE6EB', textColor: '#C05A6E', badgeBackground: '#C05A6E33' };
const SONG = { cardCategory: 'Song Poetry', backgroundColor: '#D4F2FF', textColor: '#333380', badgeBackground: '#33338033' };
const GAME = { cardCategory: 'Games', backgroundColor: '#C4EBC0', textColor: '#399918', badgeBackground: '#39991833' };
const LIFE = { cardCategory: 'Life', backgroundColor: '#FFD9B3', textColor: '#F15600', badgeBackground: '#F1560033' };
const HALLOWEEN = { cardCategory: 'Halloween', backgroundColor: '#FF8C4C', textColor: '#FFD9B3', nameColor: '#1E1E1E', badgeBackground: '#F1560066' };

function card(
  slug: string,
  title: string,
  category: ResourceCategory,
  href: string,
  extras: Partial<WebResource> = {},
): WebResource {
  const palette =
    extras.cardCategory === 'Life'
      ? LIFE
      : extras.cardCategory === 'Halloween'
        ? HALLOWEEN
        : extras.cardCategory === 'Song Poetry'
          ? SONG
          : category === 'Flashcards'
            ? FLASH
            : category === 'Grammar'
              ? GRAMMAR
              : category === 'Pronunciation'
                ? PRON
                : category === 'Storybooks'
                  ? STORY
                  : category === 'Songs'
                    ? SONG
                    : GAME;
  return { slug, title, category, href, ...palette, ...extras };
}

const flashcards: WebResource[] = [
  card('favorites', 'Favorites', 'Flashcards', '/app/flashcards/favorites', {
    virtual: true,
    mobileKey: '/(flashcards)/favorite-flashcards',
    subtitle: 'Flashcards',
  }),
  card('numbers', '1 thru 10', 'Flashcards', '/app/flashcards/numbers?range=1-10', {
    subtitle: 'Flashcards',
    mobileKey: '/(flashcards)/numbers-flashcards?numberRange=1-10',
  }),
  card('numbers', '10 thru 100', 'Flashcards', '/app/flashcards/numbers?range=10-100', {
    subtitle: 'Flashcards',
    mobileKey: '/(flashcards)/numbers-flashcards?numberRange=10-100',
  }),
  card('colors', 'Colors', 'Flashcards', '/app/flashcards/colors', { subtitle: 'Flashcards' }),
  card('pronouns', 'Pronouns', 'Flashcards', '/app/flashcards/pronouns', { subtitle: 'Flashcards' }),
  card('family', 'Family', 'Flashcards', '/app/flashcards/family', { subtitle: 'Flashcards' }),
  card('verbs', 'Verbs', 'Flashcards', '/app/flashcards/verbs', { subtitle: 'Flashcards' }),
  card('animals', 'Animals', 'Flashcards', '/app/flashcards/animals', { subtitle: 'Flashcards', ...LIFE }),
  card('food', 'Food', 'Flashcards', '/app/flashcards/food', { subtitle: 'Flashcards', ...LIFE }),
  card('places', 'Places', 'Flashcards', '/app/flashcards/places', { subtitle: 'Flashcards' }),
  card('clothing', 'Clothing', 'Flashcards', '/app/flashcards/clothing', { subtitle: 'Flashcards' }),
  card('taste', 'Taste', 'Flashcards', '/app/flashcards/taste', { subtitle: 'Flashcards' }),
  card('face-parts', 'Face Anatomy', 'Flashcards', '/app/flashcards/face-parts', { subtitle: 'Flashcards' }),
  card('internal-organs', 'Internal Organs', 'Flashcards', '/app/flashcards/internal-organs', { subtitle: 'Flashcards' }),
  card('fathers-side', "Father's Side of the Family", 'Flashcards', '/app/flashcards/fathers-side', { subtitle: 'Flashcards' }),
  card('mothers-side', "Mother's Side of the Family", 'Flashcards', '/app/flashcards/mothers-side', { subtitle: 'Flashcards' }),
  card('fathers-day', "Father's Day", 'Flashcards', '/app/flashcards/fathers-day', { subtitle: 'Flashcards' }),
  card('mothers-day', "Mother's Day", 'Flashcards', '/app/flashcards/mothers-day', { subtitle: 'Flashcards' }),
  card('december', 'Winter Flashcards', 'Flashcards', '/app/flashcards/december', { subtitle: 'Flashcards' }),
  card('halloween', 'Halloween', 'Flashcards', '/app/flashcards/halloween', {
    subtitle: 'Flashcards',
    ...HALLOWEEN,
  }),
  card('thanksgiving', 'Thanksgiving', 'Flashcards', '/app/flashcards/thanksgiving', { subtitle: 'Flashcards' }),
];

const sheets: WebResource[] = [
  card('useful-terms', 'Everyday Useful Words', 'Grammar', '/app/sheets/useful-terms?terms=words', {
    mobileKey: '/(cheatsheets)/useful-terms?terms=words',
  }),
  card('useful-terms', 'Everyday Useful Phrases', 'Grammar', '/app/sheets/useful-terms?terms=phrases', {
    mobileKey: '/(cheatsheets)/useful-terms?terms=phrases',
  }),
  card('everyday-useful-words-ii', 'Everyday Useful Words II', 'Grammar', '/app/sheets/everyday-useful-words-ii'),
  card('everyday-useful-phrases-ii', 'Everyday Useful Phrases II', 'Grammar', '/app/sheets/everyday-useful-phrases-ii'),
  card('basic-vocabulary', 'Basic Vocabulary', 'Grammar', '/app/sheets/basic-vocabulary'),
  card('asking-questions', 'Asking Questions', 'Grammar', '/app/sheets/asking-questions'),
  card('reduplication', 'Reduplication', 'Grammar', '/app/sheets/reduplication', {
    premium: true,
    addedOn: '2026-08-02T00:00:00.000Z',
  }),
  card('conjunctions', 'Conjunctions', 'Grammar', '/app/sheets/conjunctions'),
  card('negation', 'Negation - Tsis', 'Grammar', '/app/sheets/negation'),
  card('verb-tenses', 'Verb Tenses', 'Grammar', '/app/sheets/verb-tenses'),
  card('time-indicators', 'Time Indicators', 'Grammar', '/app/sheets/time-indicators'),
  card('intro-to-siab', 'Intro to Siab', 'Grammar', '/app/sheets/intro-to-siab'),
  card('subject-verb-object', 'Syntax - Forming Sentences', 'Grammar', '/app/sheets/subject-verb-object'),
  card('hmong-classifiers', 'Hmong Classifiers', 'Grammar', '/app/sheets/hmong-classifiers'),
  ...(['a', 'e', 'i', 'o', 'u', 'w'] as const).map((family) =>
    card(
      'word-families',
      `${family.toUpperCase()} Word Family`,
      'Pronunciation',
      `/app/sheets/word-families?family=${family}`,
      { mobileKey: `/(cheatsheets)/word-families?family=${family}`, ...PRON },
    ),
  ),
  card('intro-to-hmong-tones', 'Intro to Hmong Tones', 'Pronunciation', '/app/sheets/intro-to-hmong-tones'),
  card('english-vs-hmong-tones', 'English vs Hmong Tones', 'Pronunciation', '/app/sheets/english-vs-hmong-tones'),
  card('hmong-tone-examples', 'Hmong Tone Examples', 'Pronunciation', '/app/sheets/hmong-tone-examples'),
];

const storybooks: WebResource[] = [
  card('alphabet', 'Learn Hmong: Single Consonants', 'Storybooks', '/app/storybooks/alphabet'),
  card('halloween', 'Taub Mas Ntau Li Ntau', 'Storybooks', '/app/storybooks/halloween'),
  card('mai', 'A Day with Mai', 'Storybooks', '/app/storybooks/mai'),
  card('hmong-meaning', 'What does Hmong mean to you?', 'Storybooks', '/app/storybooks/hmong-meaning'),
  card('sky-high', 'Why The Sky Is Very High', 'Storybooks', '/app/storybooks/sky-high'),
  card('kuv-sov-sov', 'Kuv Sov Sov', 'Storybooks', '/app/storybooks/kuv-sov-sov', {
    subtitle: 'I Am Very Hot',
    premium: true,
  }),
];

const songs: WebResource[] = [
  card('nkauj-suab-yi', 'Nkauj Suab Yi', 'Songs', '/app/songs/nkauj-suab-yi'),
  card('greeting-a-new-friend', 'Greeting a New Friend', 'Songs', '/app/songs/greeting-a-new-friend'),
  card('maiv-lis', 'Maiv Lis', 'Songs', '/app/songs/maiv-lis'),
];

const games: WebResource[] = [
  card('match-numbers', 'Guess the Number', 'Games', '/app/games/match-numbers', {
    mobileKey: '/(games)/game-match-numbers',
  }),
  card('match-family', 'Guess the Family Member', 'Games', '/app/games/match-family', {
    mobileKey: '/(games)/game-match-family',
  }),
  card('spell-numbers', 'Spell the Numbers', 'Games', '/app/games/spell-numbers', {
    mobileKey: '/(games)/game-spell-numbers',
  }),
  card('spell-family', 'Spell the Family', 'Games', '/app/games/spell-family', {
    mobileKey: '/(games)/game-spell-family',
  }),
  card('animal-guesser', "What's the Animal?", 'Games', '/app/games/animal-guesser', {
    mobileKey: '/(games)/game-animal-guesser',
  }),
  card('body-parts', 'Match The Body Parts', 'Games', '/app/games/body-parts', {
    mobileKey: '/(games)/match-the-body-parts',
  }),
  card('build-sentence', 'Build the Sentence', 'Games', '/app/games/build-sentence', {
    mobileKey: '/(games)/game-build-sentence',
  }),
  card('practice', 'Sentence Practice', 'Games', '/app/games/practice', {
    mobileKey: '/(games)/game-sentence-practice',
  }),
  card('ya-ya', 'Ya! Ya!', 'Games', '/app/games/ya-ya', {
    subtitle: 'Fly through Hmong words',
    mobileKey: '/(games)/game-flappy-hmong',
  }),
  card('nab', 'Nab', 'Games', '/app/games/nab', {
    premium: true,
    addedOn: '2026-08-03T00:00:00.000Z',
    mobileKey: '/(games)/game-snake',
  }),
];

export const localResources: WebResource[] = [
  ...flashcards,
  ...sheets,
  ...storybooks,
  ...songs,
  card('body-parts', 'Body Parts', 'Pronunciation', '/app/diagrams/body-parts'),
  card('facial-features', 'Facial Features', 'Pronunciation', '/app/diagrams/facial-features'),
  ...games,
  card('crosswords', 'Crosswords', 'Puzzles', '/app/crosswords', {
    premium: true,
    mobileKey: '/(crosswords)/daily',
  }),
  card('stickers', 'Stickers', 'Games', '/app/stickers', {
    virtual: true,
    premium: true,
    subtitle: 'Scenes',
    mobileKey: '/(stickers)/sticker-packs',
  }),
];

export const categoryOrder: ResourceCategory[] = [
  'Flashcards',
  'Games',
  'Storybooks',
  'Grammar',
  'Pronunciation',
  'Songs',
  'Puzzles',
];

export const CATEGORY_STYLE: Record<ResourceCategory, { background: string; text: string }> = {
  Flashcards: { background: FLASH.backgroundColor, text: FLASH.textColor },
  Games: { background: GAME.backgroundColor, text: GAME.textColor },
  Storybooks: { background: STORY.backgroundColor, text: STORY.textColor },
  Grammar: { background: GRAMMAR.backgroundColor, text: GRAMMAR.textColor },
  Pronunciation: { background: PRON.backgroundColor, text: PRON.textColor },
  Songs: { background: SONG.backgroundColor, text: SONG.textColor },
  Puzzles: { background: GAME.backgroundColor, text: GAME.textColor },
};
